import { useState } from "react";
import { Card, CardContent } from "../../../components/ui-custom/Card";
import Button from "components/ui-custom/Button";
import { Badge } from "../../../components/ui-custom/Badge";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  GripVertical,
  Link,
  Link2,
  Pencil,
  Trash2,
} from "lucide-react";
import { formatCurrency, getSectionTotals } from "./boqData";
import EllipsisMenu from "../../../components/UI_components/EllipsisMenu";

const statusColors = {
  planned: "bg-muted text-muted-foreground",
  "in-progress": "bg-warning/10 text-warning border-warning/30",
  completed: "bg-success/10 text-success border-success/30",
  ongoing: "bg-warning/10 text-warning border-warning/30",
  "Not started": "bg-muted text-muted-foreground",
};

const getActualProfitPercent = (estimatedTotalCost, actualTotalCost) =>
  estimatedTotalCost > 0
    ? ((estimatedTotalCost - actualTotalCost) * 100) / estimatedTotalCost
    : 0;

const BoQSectionCard = ({
  section,
  onAddItem,
  onEditItem,
  onEditSection,
  onDeleteSection,
  onLinkSection,
  onDeleteItem,
  onLinkItem,
}) => {
  const [expanded, setExpanded] = useState(true);
  const totals = getSectionTotals(section);

  const activeItems = section.items.filter((item) => item.status !== "planned").length;
  const progressPercent = section.items.length > 0 ? Math.round((activeItems / section.items.length) * 100) : 0;

  return (
    <Card className="border-border/60 shadow-none">
      <div className="flex items-center gap-2 p-2 hover:bg-muted/30 transition-colors">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center justify-between"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex min-w-0 items-center gap-3">
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <div className="h-8 w-8 rounded-lg bg-sm-primary/10 flex items-center justify-center text-sm font-bold text-sm-primary">
              {section.code}
            </div>
            <div className="min-w-0 text-left">
              <h3 className="text-sm font-semibold text-foreground uppercase truncate">
                {section.name}
              </h3>
              <p className="text-xs text-muted-foreground">
                {section.items.length} items · {progressPercent}% active
              </p>
            </div>
          </div>
          <div className="flex items-center gap-6 text-right">
            <div>
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Budget</p>
              <p className="text-sm font-semibold text-foreground">{formatCurrency(totals.totalBudget)}</p>
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Actual</p>
              <p className={`text-sm font-semibold ${totals.totalActual > totals.totalBudget ? "text-destructive" : "text-success"}`}>
                {formatCurrency(totals.totalActual)}
              </p>
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Variance</p>
              <p className={`text-sm font-semibold ${totals.totalBudget - totals.totalActual >= 0 ? "text-success" : "text-destructive"}`}>
                {formatCurrency(Math.abs(totals.totalBudget - totals.totalActual))}
              </p>
            </div>
          </div>
        </button>
        <div className="shrink-0">
          <EllipsisMenu
            buttonAriaLabel={`Actions for ${section.name}`}
            triggerClassName="h-7 w-7 rounded-md text-muted-foreground hover:bg-muted"
            items={[
              {
                key: "edit",
                label: "Edit",
                icon: Pencil,
                onClick: () => onEditSection?.(section),
              },
              {
                key: "link",
                label: "Link",
                icon: Link2,
                hidden: section.taskGroup != null,
                onClick: () => onLinkSection?.(section),
              },
              {
                key: "delete",
                label: "Delete",
                icon: Trash2,
                danger: true,
                onClick: () => onDeleteSection?.(section),
              },
            ]}
          />
        </div>
      </div>

      {expanded && (
        <CardContent className="p-0 border-t border-border/40">
          <div className="grid grid-cols-[32px_60px_minmax(0,1fr)_60px_70px_90px_90px_85px_110px_90px_85px_44px] gap-2 px-4 py-2.5 bg-muted/30 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
            <span />
            <span>Code</span>
            <span>Item</span>
            <span className="text-center">Qty</span>
            <span className="text-center">Unit</span>
            <span className="text-center">Material</span>
            <span className="text-center">Labour</span>
            <span className="text-center">Total</span>
            <span className="text-center">Est. Profit</span>
            <span className="text-center">Actual Profit</span>
            <span className="text-center">Status</span>
            <span />
          </div>

          {section.items.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">No items added yet.</div>
          ) : (
            section.items.map((item) => {
              const actualProfit = getActualProfitPercent(item.totalCost, item.actualCost);
              const actualProfitColor =
                actualProfit < 0
                  ? "text-destructive"
                  : actualProfit > 0
                    ? "text-success"
                    : "text-muted-foreground";

              return (
                <div
                  key={item.id}
                  className="grid grid-cols-[32px_60px_minmax(0,1fr)_60px_70px_90px_90px_85px_110px_90px_85px_44px] gap-2 px-4 py-2.5 border-t border-border/20 hover:bg-muted/20 items-center group text-sm"
                >
                  <GripVertical className="h-3.5 w-3.5 text-muted-foreground/40 opacity-0 group-hover:opacity-100" />
                  <span className="text-xs font-mono text-sm-primary font-medium">{item.code}</span>
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-foreground truncate max-w-[220px]">
                      {item.description}
                    </span>

                    {item.taskName && (
                      <span
                        className="flex-shrink-0 text-xs text-muted-foreground cursor-pointer"
                        title={item.taskName}
                      >
                        <Link className="h-3.5 w-3.5" color="#3B0069" />
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-center text-muted-foreground">{item.unit}</span>
                  <span className="text-sm text-center">{item.quantity}</span>
                  <span className="text-xs text-center text-muted-foreground">{formatCurrency(item.materialCost)}</span>
                  <span className="text-xs text-center text-muted-foreground">{formatCurrency(item.labourCost)}</span>
                  <span className="text-sm text-center font-medium">{formatCurrency(item.totalCost)}</span>
                  <span className="text-xs text-center">{Number(item.profitPercent || 0).toFixed(1)}%</span>
                  <span className={`text-xs text-center font-medium ${actualProfitColor}`}>
                    {actualProfit.toFixed(1)}%
                  </span>
                  <div className="flex items-center justify-center gap-1">
                    <Badge className={`${statusColors[item.status]} text-[10px] px-1.5 py-0`}>
                      {item.status}
                    </Badge>
                  </div>
                  <div className="relative z-10 flex items-center justify-end">
                    <EllipsisMenu
                      buttonAriaLabel={`Actions for ${item.description}`}
                      triggerClassName="h-7 w-7 rounded-md text-muted-foreground hover:bg-muted"
                      menuClassName="top-auto bottom-8"
                      items={[
                        {
                          key: "edit",
                          label: "Edit",
                          icon: Pencil,
                          onClick: () => onEditItem?.(section, item),
                        },
                        {
                          key: "link",
                          label: "Link",
                          icon: Link2,
                          hidden: item.taskId != null,
                          onClick: () => onLinkItem?.(section, item),
                        },
                        {
                          key: "delete",
                          label: "Delete",
                          icon: Trash2,
                          danger: true,
                          onClick: () => onDeleteItem?.(section, item),
                        },
                      ]}
                    />
                  </div>
                </div>
              );
            })
          )}

          <div className="px-4 py-2.5 border-t border-border/20">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-sm-primary gap-1.5 hover:bg-sm-primary/5"
              onClick={() => onAddItem(section.id)}
            >
              <Plus className="h-3.5 w-3.5" /> Add Item
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default BoQSectionCard;
